/**
 * API Health Check System
 * Provides centralized testing and status monitoring for all API integrations
 */

export type ApiStatus = 'connected' | 'configured' | 'unconfigured' | 'error' | 'testing';

export interface ApiHealthResult {
  status: ApiStatus;
  message: string;
  lastChecked?: Date;
  error?: string;
  details?: Record<string, any>;
}

export interface ApiHealthCheck {
  name: string;
  category: 'seo' | 'google' | 'social' | 'commerce' | 'infrastructure';
  result: ApiHealthResult;
}

// ============================================================================
// DataForSEO Health Check
// ============================================================================

export async function checkDataForSEOHealth(): Promise<ApiHealthResult> {
  const login = import.meta.env.VITE_DATAFORSEO_LOGIN;
  const password = import.meta.env.VITE_DATAFORSEO_PASSWORD;

  if (!login || !password) {
    return {
      status: 'unconfigured',
      message: 'DataForSEO credentials not configured',
      details: {
        hasLogin: !!login,
        hasPassword: !!password
      }
    };
  }

  try {
    // Test connection by fetching user data
    const credentials = btoa(`${login}:${password}`);
    const response = await fetch('https://api.dataforseo.com/v3/appendix/user_data', {
      method: 'GET',
      headers: {
        'Authorization': `Basic ${credentials}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      return {
        status: 'error',
        message: `DataForSEO API error: ${response.statusText}`,
        lastChecked: new Date(),
        error: `HTTP ${response.status}`
      };
    }

    const data = await response.json();
    
    if (data.status_code === 20000) {
      return {
        status: 'connected',
        message: 'DataForSEO connected successfully',
        lastChecked: new Date(),
        details: {
          tasksCount: data.tasks_count,
          cost: data.cost
        }
      };
    }

    return {
      status: 'error',
      message: 'DataForSEO authentication failed',
      lastChecked: new Date(),
      error: data.status_message
    };
  } catch (error) {
    return {
      status: 'error',
      message: 'Failed to connect to DataForSEO',
      lastChecked: new Date(),
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

// ============================================================================
// Google APIs Health Check
// ============================================================================

export async function checkGoogleApisHealth(): Promise<ApiHealthResult> {
  const apiKey = import.meta.env.VITE_GOOGLE_API_KEY;

  if (!apiKey || apiKey === 'your_google_api_key_here') {
    return {
      status: 'unconfigured',
      message: 'Google API key not configured'
    };
  }

  try {
    // Test with PageSpeed Insights API (free, no quota issues)
    const testUrl = 'https://www.google.com';
    const response = await fetch(
      `https://www.googleapis.com/pagespeedonline/v5/runPagespeed?url=${encodeURIComponent(testUrl)}&key=${apiKey}&strategy=mobile`
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        status: 'error',
        message: `Google API error: ${response.statusText}`,
        lastChecked: new Date(),
        error: errorData.error?.message || `HTTP ${response.status}`
      };
    }

    const data = await response.json();
    
    if (data.lighthouseResult) {
      return {
        status: 'connected',
        message: 'Google APIs connected successfully',
        lastChecked: new Date()
      };
    }

    return {
      status: 'error',
      message: 'Google API returned unexpected response',
      lastChecked: new Date()
    };
  } catch (error) {
    return {
      status: 'error',
      message: 'Failed to connect to Google APIs',
      lastChecked: new Date(),
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

// ============================================================================
// Google Ads OAuth Health Check
// ============================================================================

export async function checkGoogleAdsHealth(): Promise<ApiHealthResult> {
  const clientId = import.meta.env.VITE_GOOGLE_ADS_CLIENT_ID;
  const clientSecret = import.meta.env.VITE_GOOGLE_ADS_CLIENT_SECRET;
  const refreshToken = import.meta.env.VITE_GOOGLE_ADS_REFRESH_TOKEN;
  const developerToken = import.meta.env.VITE_GOOGLE_ADS_DEVELOPER_TOKEN;

  const hasAllCredentials = clientId && clientSecret && refreshToken && developerToken;

  if (!hasAllCredentials) {
    return {
      status: 'unconfigured',
      message: 'Google Ads OAuth credentials not fully configured',
      details: {
        hasClientId: !!clientId,
        hasClientSecret: !!clientSecret,
        hasRefreshToken: !!refreshToken,
        hasDeveloperToken: !!developerToken
      }
    };
  }

  // OAuth credentials are configured, but we need backend to test actual connection
  // since the Google Ads API requires server-side calls
  return {
    status: 'configured',
    message: 'Google Ads credentials configured (backend validation required)',
    details: {
      note: 'OAuth credentials present, actual API testing requires backend service'
    }
  };
}

// ============================================================================
// Supabase Health Check
// ============================================================================

export async function checkSupabaseHealth(): Promise<ApiHealthResult> {
  const url = import.meta.env.VITE_SUPABASE_URL;
  const key = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!url || !key) {
    return {
      status: 'unconfigured',
      message: 'Supabase credentials not configured',
      details: {
        hasUrl: !!url,
        hasKey: !!key
      }
    };
  }

  try {
    // Test connection by attempting to connect
    const { createClient } = await import('@supabase/supabase-js');
    const supabase = createClient(url, key);
    
    // Simple query to test connection
    const { error } = await supabase.from('marketing_campaigns').select('id').limit(1);

    if (error) {
      // Check if it's an auth error vs connection error
      if (error.message.includes('permission') || error.message.includes('policy')) {
        // RLS policy issue, but connection works
        return {
          status: 'connected',
          message: 'Supabase connected (RLS policies active)',
          lastChecked: new Date()
        };
      }

      return {
        status: 'error',
        message: 'Supabase connection failed',
        lastChecked: new Date(),
        error: error.message
      };
    }

    return {
      status: 'connected',
      message: 'Supabase connected successfully',
      lastChecked: new Date()
    };
  } catch (error) {
    return {
      status: 'error',
      message: 'Failed to connect to Supabase',
      lastChecked: new Date(),
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

// ============================================================================
// Facebook API Health Check
// ============================================================================

export async function checkFacebookHealth(): Promise<ApiHealthResult> {
  const accessToken = import.meta.env.VITE_FACEBOOK_PAGE_ACCESS_TOKEN;
  const pageId = import.meta.env.VITE_FACEBOOK_PAGE_ID;

  if (!accessToken || !pageId) {
    return {
      status: 'unconfigured',
      message: 'Facebook credentials not configured',
      details: {
        hasAccessToken: !!accessToken,
        hasPageId: !!pageId
      }
    };
  }

  // Facebook requires backend validation
  return {
    status: 'configured',
    message: 'Facebook credentials configured (backend validation required)',
    details: {
      note: 'Facebook Graph API testing requires backend service'
    }
  };
}

// ============================================================================
// Run All Health Checks
// ============================================================================

export async function runAllHealthChecks(): Promise<ApiHealthCheck[]> {
  const checks: ApiHealthCheck[] = [
    {
      name: 'DataForSEO',
      category: 'seo',
      result: await checkDataForSEOHealth()
    },
    {
      name: 'Google APIs',
      category: 'google',
      result: await checkGoogleApisHealth()
    },
    {
      name: 'Google Ads',
      category: 'google',
      result: await checkGoogleAdsHealth()
    },
    {
      name: 'Supabase',
      category: 'infrastructure',
      result: await checkSupabaseHealth()
    },
    {
      name: 'Facebook',
      category: 'social',
      result: await checkFacebookHealth()
    }
  ];

  return checks;
}

// ============================================================================
// Helper Functions
// ============================================================================

export function getStatusColor(status: ApiStatus): string {
  switch (status) {
    case 'connected':
      return 'green';
    case 'configured':
      return 'blue';
    case 'unconfigured':
      return 'gray';
    case 'error':
      return 'red';
    case 'testing':
      return 'yellow';
    default:
      return 'gray';
  }
}

export function getStatusIcon(status: ApiStatus): string {
  switch (status) {
    case 'connected':
      return '✓';
    case 'configured':
      return '◐';
    case 'unconfigured':
      return '○';
    case 'error':
      return '✕';
    case 'testing':
      return '⟳';
    default:
      return '?';
  }
}

export function getStatusLabel(status: ApiStatus): string {
  switch (status) {
    case 'connected':
      return 'Connected';
    case 'configured':
      return 'Configured';
    case 'unconfigured':
      return 'Not Configured';
    case 'error':
      return 'Error';
    case 'testing':
      return 'Testing...';
    default:
      return 'Unknown';
  }
}
